import 'core-js/modules/es.symbol.js';
import 'core-js/modules/es.symbol.description.js';
import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.map.js';
import 'core-js/modules/es.array.slice.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.promise.js';
import 'core-js/modules/es.string.link.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { Children, useRef, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { EllipsisHorizontalIcon, ChevronUpSmallIcon, ChevronDownSmallIcon } from '@cognite/cogs-icons';
import { useResizeObserver, isMobileView, getThemeClassNames, IconWrapper } from '@cognite/cogs-utils';
import '../../tokens/color-tokens/index.js';
import '../../tokens/elevation-tokens/index.js';
import { Body } from '../../tokens/typography/typography.js';
import 'tslib';
import { Flex } from '../flex/flex.js';
import { Tooltip } from '../tooltip/tooltip.js';
import { IconButton } from '../_technical/icon-button/icon-button.js';
import { Button } from '../button/button.js';
import { useConfigContextLoose } from '../config-provider/config-provider.js';
import { Dropdown } from '../dropdown/dropdown.js';
import { Menu } from '../menu/menu.js';
import { useResponsiveMaxWidth } from './use-responsive-max-width.js';

var Separator = function Separator() {
  return jsx(Body, {
    className: "cogs-v10 cogs-breadcrumbs__separator",
    children: "/"
  });
};
/**
 * Breadcrumbs
 * =============================================================================
 * Breadcrumbs is secondary navigation displaying hierarchy or navigational path.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-breadcrumb--base)
 *
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Breadcrumb>
 *   <Breadcrumbs.Item title="Home" link="#" icon={<HomeIcon />} />
 *   <Breadcrumbs.Item title="Assets" link="#" />
 *   <Breadcrumbs.Item title="Industrial" link="#" />
 *   <Breadcrumbs.Item title="Current" link="#" active />
 * </Breadcrumb>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-breadcrumb--base)
 */
var _Breadcrumbs = function Breadcrumbs(_ref) {
  var children = _ref.children,
    className = _ref.className,
    isCollapsed = _ref.isCollapsed,
    isResponsive = _ref.isResponsive,
    inverted = _ref.inverted,
    themeProps = _ref.themeProps;
  var childrenArr = Children.toArray(children);
  var limit = 1;
  var items = childrenArr.map(function (child) {
    return /*#__PURE__*/React.isValidElement(child) ? child.props : null;
  });
  var _React$useState = React.useState(false),
    isDropdownVisible = _React$useState[0],
    setIsDropdownVisible = _React$useState[1];
  var mainWrapperRef = useRef(null);
  var mainWrapperSize = useResizeObserver(mainWrapperRef);
  var _useState = useState(mainWrapperSize.scrollWidth),
    nonCollapsedRealScrollWidth = _useState[0],
    setNonCollapsedRealScrollWidth = _useState[1];
  var _useState2 = useState(!!isCollapsed),
    shouldCollapseDueToOverflow = _useState2[0],
    setShouldCollapseDueToOverflow = _useState2[1];
  var _useState3 = useState(false),
    rendered = _useState3[0],
    setRendered = _useState3[1];
  useEffect(function () {
    if (!rendered) return setRendered(true);
    if (!isResponsive || isCollapsed !== undefined) return setShouldCollapseDueToOverflow(!!isCollapsed);
    if (!mainWrapperRef.current) return;
    // the reason for not using values from resize observer here is, that in
    // case of quick size change e.g. show/hide of a sidebar, the measurements
    // happen too quickly and hold stale values, so we need to read fresh
    var clientWidth = mainWrapperRef.current.clientWidth;
    var scrollWidth = mainWrapperRef.current.scrollWidth;
    if (!shouldCollapseDueToOverflow) {
      var isScrolling = scrollWidth > clientWidth;
      setShouldCollapseDueToOverflow(isScrolling);
      // we save this here to later compare latest client width with original
      // scroll width to be able to undo the collapse if not needed anymore
      setNonCollapsedRealScrollWidth(scrollWidth);
    } else {
      setShouldCollapseDueToOverflow(nonCollapsedRealScrollWidth >= clientWidth);
    }
  }, [mainWrapperSize, isCollapsed, shouldCollapseDueToOverflow, isResponsive, nonCollapsedRealScrollWidth, rendered, items.length]);
  // when the number of items change, we have to de-collapse to measure the
  // original scroll width in the upper useEffect and decide whether to collapse
  // based on new measurements or not
  useEffect(function () {
    setShouldCollapseDueToOverflow(false);
  }, [items.length]);
  var renderListItems = function renderListItems(_ref2) {
    var _ref2$start = _ref2.start,
      start = _ref2$start === undefined ? 0 : _ref2$start,
      itemLimit = _ref2.itemLimit,
      _ref2$incrementer = _ref2.incrementer,
      incrementer = _ref2$incrementer === undefined ? 0 : _ref2$incrementer;
    return jsx(Fragment, {
      children: items.slice(start, itemLimit).map(function (child, i) {
        var isCurrent = i + 1 + incrementer === items.length;
        return jsx(_Breadcrumbs.Item, Object.assign({}, child, {
          active: isCurrent,
          "aria-current": isCurrent ? 'page' : 'false',
          _parentWrapperRef: isResponsive ? mainWrapperRef : undefined
        }), child.label + "_" + child.icon + "_" + child.link);
      })
    });
  };
  var isCollapsedMode = useMemo(function () {
    return isMobileView() || isCollapsed || shouldCollapseDueToOverflow;
  }, [isCollapsed, shouldCollapseDueToOverflow]);
  return jsx("nav", {
    ref: mainWrapperRef,
    "aria-label": "Breadcrumbs",
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-breadcrumbs', className].concat(getThemeClassNames(inverted, themeProps))),
    children: jsx(Flex, {
      element: "ol",
      direction: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      children: items.length > 2 && isCollapsedMode ? jsxs(Fragment, {
        children: [renderListItems({
          itemLimit: limit
        }), jsx("div", {
          className: "cogs-v10 cogs-breadcrumbs__menu__wrapper",
          children: jsx(Dropdown, {
            hideOnSelect: {
              hideOnContentClick: true,
              hideOnOutsideClick: true
            },
            onShow: function onShow() {
              return setIsDropdownVisible(true);
            },
            onHide: function onHide() {
              return setIsDropdownVisible(false);
            },
            offset: [0, 4],
            content: jsx(Menu, {
              children: items.slice(limit, items.length - limit).map(function (child) {
                return jsx(Menu.Item, {
                  onClick: child.onClick,
                  icon: child.icon,
                  iconPlacement: "left",
                  href: child.link,
                  children: child.label
                }, child.label);
              })
            }),
            children: jsx("button", {
              type: "button",
              className: "cogs-v10 cogs-breadcrumbs__item__button cogs-breadcrumbs__dropdown-toggle",
              "aria-pressed": isDropdownVisible,
              "data-testId": "cogs-breadcrumbs-collapsed-dropdown",
              children: jsx(IconWrapper, {
                size: 12,
                children: jsx(EllipsisHorizontalIcon, {})
              })
            })
          })
        }), jsx(Separator, {}), renderListItems({
          start: items.length - limit,
          itemLimit: items.length,
          incrementer: items.length - limit
        })]
      }) : renderListItems({
        itemLimit: items.length
      })
    })
  });
};
var BreadcrumbsActiveItemContent = function BreadcrumbsActiveItemContent(_ref3) {
  var activeOptions = _ref3.activeOptions,
    children = _ref3.children,
    disabled = _ref3.disabled,
    dropdownAppendTo = _ref3.dropdownAppendTo,
    icon = _ref3.icon,
    label = _ref3.label,
    onClick = _ref3.onClick,
    _parentWrapperRef = _ref3._parentWrapperRef;
  var _a, _b;
  var _useState4 = useState(false),
    isDropdownVisible = _useState4[0],
    setIsDropdownVisible = _useState4[1];
  var _ref4 = activeOptions || {},
    description = _ref4.description,
    promoChip = _ref4.promoChip,
    tagChip = _ref4.tagChip,
    statusChip = _ref4.statusChip,
    actionMenu = _ref4.actionMenu,
    _ref4$actionMenuTarge = _ref4.actionMenuTarget,
    actionMenuTarget = _ref4$actionMenuTarge === undefined ? 'icon' : _ref4$actionMenuTarge;
  var labelRef = useRef(null);
  var labelContainerRef = useRef(null);
  var descriptionRef = useRef(null);
  var labelMaxWidth = useResponsiveMaxWidth(_parentWrapperRef !== null && _parentWrapperRef !== undefined ? _parentWrapperRef : {
    current: null
  }, labelContainerRef, labelRef);
  var descriptionMaxWidth = useResponsiveMaxWidth(_parentWrapperRef !== null && _parentWrapperRef !== undefined ? _parentWrapperRef : {
    current: null
  }, descriptionRef, descriptionRef);
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  var renderLabel = function renderLabel() {
    return jsx(Tooltip, {
      content: label,
      disabled: !labelMaxWidth,
      placement: "bottom",
      children: jsx("span", {
        ref: labelRef,
        className: "cogs-v10 cogs-breadcrumbs__item--active__label",
        style: {
          maxWidth: labelMaxWidth && "max(80px, " + labelMaxWidth + ")"
        },
        children: label
      })
    });
  };
  var renderDescription = function renderDescription() {
    return jsx(Tooltip, {
      content: description,
      disabled: !descriptionMaxWidth,
      placement: "bottom",
      children: jsx(Body, {
        ref: descriptionRef,
        className: "cogs-v10 cogs-breadcrumbs__item--active__description",
        size: "xx-small",
        muted: true,
        style: {
          maxWidth: descriptionMaxWidth && "max(80px, " + descriptionMaxWidth + ")"
        },
        children: description
      })
    });
  };
  if (actionMenuTarget === 'fullItem') {
    return jsxs(Flex, {
      direction: "column",
      children: [jsxs(Flex, {
        ref: labelContainerRef,
        gap: 8,
        alignItems: "center",
        className: classNames('cogs-v10', 'cogs-breadcrumbs__item__container', 'cogs-breadcrumbs__item__container--active'),
        children: [actionMenu ? jsx(Dropdown, {
          appendTo: (_a = dropdownAppendTo !== null && dropdownAppendTo !== undefined ? dropdownAppendTo : getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer()) !== null && _a !== undefined ? _a : undefined,
          hideOnSelect: {
            hideOnContentClick: true,
            hideOnOutsideClick: true
          },
          onShow: function onShow() {
            return setIsDropdownVisible(true);
          },
          onHide: function onHide() {
            return setIsDropdownVisible(false);
          },
          content: actionMenu,
          offset: [0, 4],
          children: jsxs(Button, {
            tabIndex: 0,
            className: classNames('cogs-v10', 'cogs-breadcrumbs__item__button', 'cogs-breadcrumbs__item__button--active', disabled && 'cogs-breadcrumbs__item__button--disabled'),
            type: "ghost",
            "aria-haspopup": actionMenu ? 'menu' : 'false',
            "aria-pressed": isDropdownVisible,
            "aria-current": 'page',
            onClick: onClick,
            disabled: disabled,
            toggled: isDropdownVisible,
            children: [icon && jsx(IconWrapper, {
              size: 14,
              className: "cogs-v10 cogs-breadcrumbs__icon-wrapper",
              children: icon
            }), children !== null && children !== undefined ? children : label && renderLabel(), isDropdownVisible ? jsx(ChevronUpSmallIcon, {}) : jsx(ChevronDownSmallIcon, {})]
          })
        }) : null, promoChip, tagChip, statusChip]
      }), description && renderDescription()]
    });
  }
  return jsxs(Flex, {
    direction: "column",
    children: [jsxs("div", {
      ref: labelContainerRef,
      className: classNames('cogs-v10', 'cogs-breadcrumbs__item__container', 'cogs-breadcrumbs__item__container--active'),
      children: [jsxs("div", {
        className: classNames('cogs-v10', 'cogs-breadcrumbs__item__button', 'cogs-breadcrumbs__item__button--active', disabled && 'cogs-breadcrumbs__item__button--disabled'),
        onClick: onClick,
        children: [icon && jsx(IconWrapper, {
          size: 14,
          className: "cogs-v10 cogs-breadcrumbs__icon-wrapper",
          children: icon
        }), children !== null && children !== undefined ? children : label && renderLabel()]
      }), actionMenu && jsx(Dropdown, {
        appendTo: (_b = dropdownAppendTo !== null && dropdownAppendTo !== undefined ? dropdownAppendTo : getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer()) !== null && _b !== undefined ? _b : undefined,
        hideOnSelect: {
          hideOnContentClick: true,
          hideOnOutsideClick: true
        },
        onShow: function onShow() {
          return setIsDropdownVisible(true);
        },
        onHide: function onHide() {
          return setIsDropdownVisible(false);
        },
        content: actionMenu,
        offset: [0, 4],
        className: "cogs-v10 cogs-breadcrumbs__item--active__dropdown",
        children: jsx(IconButton, {
          icon: isDropdownVisible ? ChevronUpSmallIcon : ChevronDownSmallIcon,
          toggled: isDropdownVisible
        })
      }), promoChip, tagChip, statusChip]
    }), description && renderDescription()]
  });
};
var BreadcrumbsItem = function BreadcrumbsItem(props) {
  var className = props.className,
    label = props.label,
    onClick = props.onClick,
    link = props.link,
    icon = props.icon,
    active = props.active,
    disabled = props.disabled,
    children = props.children;
  var handleClick = function handleClick(e) {
    if (onClick && !disabled) onClick(e);
  };
  var renderInactiveItem = function renderInactiveItem() {
    return jsxs(Fragment, {
      children: [jsxs("a", {
        tabIndex: 0,
        className: classNames('cogs-v10', 'cogs-breadcrumbs__item__button', disabled && 'cogs-breadcrumbs__item__button--disabled'),
        "aria-current": 'false',
        href: link,
        onClick: handleClick,
        children: [icon && jsx(IconWrapper, {
          size: 14,
          className: "cogs-v10 cogs-breadcrumbs__icon-wrapper",
          children: icon
        }), children !== null && children !== undefined ? children : label && jsx("span", {
          children: label
        })]
      }), jsx(Separator, {})]
    });
  };
  return jsx(Flex, {
    element: "li",
    direction: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    className: classNames('cogs-v10', 'cogs-breadcrumbs__item', className),
    "data-chmln": props['data-chmln'],
    children: active ? jsx(BreadcrumbsActiveItemContent, Object.assign({}, props, {
      onClick: handleClick
    })) : renderInactiveItem()
  });
};
_Breadcrumbs.Item = BreadcrumbsItem;

export { _Breadcrumbs as Breadcrumbs, BreadcrumbsItem };
